import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable, startWith, take } from 'rxjs';

import { ConfigService, NativeBridgeService } from '@fcom/core/services';
import { cartData } from '@fcom/booking/store/selectors';
import { MultivariateTestService } from '@fcom/common/multivariate-test/services/multivariate-test.service';
import { MultivariateTestId, TestVariant } from '@fcom/common/interfaces';
import { isAwardBooking } from '@fcom/common-booking/utils/common-booking.utils';
import { activeTab } from '@fcom/booking-widget/store';
import { WidgetTab } from '@fcom/booking-widget/interfaces';

interface ShoppingCartBoundOptions {
  isDirect?: boolean;
  boundIndex?: number;
  isMulticity?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ShoppingCartService {
  configService = inject(ConfigService);
  multivariateTestService = inject(MultivariateTestService);
  nativeBridgeService = inject(NativeBridgeService);
  store$ = inject(Store);

  readonly isEnabled: Signal<boolean> = signal<boolean>(false);
  readonly isOpen = signal<boolean>(false);

  private readonly boundSelections = new Map<number, boolean>();

  private readonly isTestVariantB: Signal<boolean> = signal<boolean>(false);
  private readonly isAward: Signal<boolean> = signal<boolean>(false);
  private readonly isMulticity: WritableSignal<boolean> = signal<boolean>(false);
  private readonly allBoundsDirect: WritableSignal<boolean> = signal<boolean>(false);

  constructor() {
    if (!this.configService.cfg.enableShoppingCart) {
      return;
    }

    this.isAward = toSignal(this.isAward$(), { requireSync: true });

    this.isTestVariantB = toSignal(
      this.multivariateTestService.getTestVariant(MultivariateTestId.SHOPPING_CART).pipe(
        map(({ variant }) => {
          return variant === TestVariant.B;
        })
      )
    );

    this.store$.pipe(cartData(), filter(Boolean), take(1)).subscribe((cart) => {
      this.allBoundsDirect.set(cart.bounds.every((bound) => bound.flights === 1));
    });

    this.isEnabled = computed(
      () =>
        this.isTestVariantB() &&
        this.allBoundsDirect() &&
        !this.isAward() &&
        !this.isMulticity() &&
        !this.nativeBridgeService.isInsideNativeWebView
    );
  }

  updateBoundSelection({ boundIndex, isDirect, isMulticity }: ShoppingCartBoundOptions): void {
    if (!this.configService.cfg.enableShoppingCart) {
      return;
    }

    this.isMulticity.set(isMulticity);

    if (isMulticity) {
      return;
    }

    if (boundIndex === 0) {
      this.boundSelections.clear();
    }

    this.boundSelections.set(boundIndex, isDirect);
    this.allBoundsDirect.set([...this.boundSelections.values()].every((direct) => direct));
  }

  setVisible(visible: boolean): void {
    this.isOpen.set(visible);
  }

  private isAward$(): Observable<boolean> {
    return combineLatest([
      this.store$.pipe(activeTab(), startWith(null)),
      this.store$.pipe(cartData(), startWith(null)),
    ]).pipe(
      map(([tab, cart]) => {
        if (tab) {
          return tab === WidgetTab.AWARD;
        }
        if (cart) {
          return isAwardBooking(cart.prices.unpaid);
        }
        return false;
      })
    );
  }
}
